import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useEffect, useRef, useState } from 'react';
import type { AggregatesArtifact, ResponsesArtifact } from '../../data/types';
import { cn } from '../../lib/cn';
import { useAppMode } from '../../state/appMode';
import { PresenterProgress } from '../../state/PresenterProgress';
import { useMotionPreferences } from '../../motion/preferences';
import type { OnboardingAnswers, VisitCohort } from './cohort';
import { matchingRespondentIds, VISIT_OPTIONS } from './cohort';

type Step = 'arrival' | 'visit' | 'age' | 'result';
gsap.registerPlugin(useGSAP);
const AGE_BANDS = ['10代以下', '20代', '30代', '40代', '50代', '60代', '70代以上'] as const;

interface OnboardingShellProps {
  aggregates: AggregatesArtifact;
  responses: ResponsesArtifact | null;
  answers: OnboardingAnswers;
  onAnswersChange(answers: OnboardingAnswers): void;
  onComplete(): void;
}

function OptionButton({
  selected,
  title,
  note,
  onClick,
}: {
  selected: boolean;
  title: string;
  note?: string;
  onClick(): void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'group min-h-24 rounded-2xl border px-5 py-4 text-left transition duration-300',
        selected
          ? 'border-atc-primary bg-atc-primary text-white shadow-atc-card'
          : 'border-atc-navy/15 bg-white/85 text-atc-ink hover:border-atc-primary hover:bg-atc-surface',
      )}
    >
      <span className="block text-lg font-bold">{title}</span>
      {note && <span className={cn('mt-1 block text-xs', selected ? 'text-white/75' : 'text-atc-slate')}>{note}</span>}
    </button>
  );
}

export function OnboardingShell({
  aggregates,
  responses,
  answers,
  onAnswersChange,
  onComplete,
}: OnboardingShellProps) {
  const [step, setStep] = useState<Step>('arrival');
  const panelRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const { mode } = useAppMode();
  const { reducedMotion } = useMotionPreferences();
  const matchCount = responses ? matchingRespondentIds(responses, answers)?.size : undefined;
  const ageBands = responses
    ? AGE_BANDS.filter((age) => responses.codebook.age_group.includes(age))
    : [];

  const chooseVisit = (visit: VisitCohort) => {
    onAnswersChange({ ...answers, visit });
    setStep('age');
  };
  const chooseAge = (age: string) => {
    onAnswersChange({ ...answers, age });
    setStep('result');
  };

  useEffect(() => {
    if (step === 'arrival') return;
    requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>('[data-step-heading]')?.focus();
    });
  }, [step]);

  useGSAP(() => {
    if (reducedMotion) return;
    const timeline = gsap.timeline({ defaults: { ease: 'power3.out', overwrite: 'auto' } });
    timeline.fromTo('p', { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.22, stagger: 0.18 })
      .fromTo('h1 span', { autoAlpha: 0, y: 22 }, { autoAlpha: 1, y: 0, duration: 0.44, stagger: 0.045 }, '-=0.14');
    return () => timeline.kill();
  }, { scope: heroRef, dependencies: [reducedMotion], revertOnUpdate: true });

  return (
    <main className="relative z-10 min-h-svh overflow-hidden text-atc-ink">
      <PresenterProgress current={0} total={6} label="INTRO" />
      <section className="flex min-h-svh items-center px-6 py-24 md:px-12 lg:px-20">
        <div className="mx-auto grid w-full max-w-[90rem] gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,34rem)] lg:items-end">
          <div ref={heroRef} className="max-w-4xl">
            <p className="mb-6 text-xs font-bold uppercase tracking-[0.34em] text-atc-slate">ATC SUMMER LOTTERY SURVEY · 2026</p>
            <h1 className="text-display-sm font-bold">
              <span className="block text-atc-primary">{aggregates.total.toLocaleString('ja-JP')}</span>
              <span className="block">人の声から、</span>
              <span className="block">次のATCを読む。</span>
            </h1>
            <p className="mt-8 max-w-xl text-base font-medium leading-loose text-atc-slate md:text-lg">
              一人ひとりの回答を、ひとつの点として描いています。二つの質問で、あなたに近い回答者を探してみてください。
            </p>
          </div>

          <div ref={panelRef} className="atc-material relative rounded-3xl p-6 shadow-atc-card md:p-8" aria-live="polite">
            {step === 'arrival' && (
              <div>
                <p className="text-sm font-bold text-atc-primary">POPULATION ARRIVAL</p>
                <h2 className="mt-4 text-2xl font-bold leading-snug">1,386人の中に、あなたに近い人はいるだろうか。</h2>
                <p className="mt-4 text-sm leading-relaxed text-atc-slate">
                  回答はこの画面の中だけで使われ、保存されません。
                </p>
                <button
                  type="button"
                  disabled={!responses}
                  onClick={() => setStep('visit')}
                  className="mt-8 w-full rounded-full bg-atc-navy px-6 py-4 font-bold text-white transition hover:bg-atc-primary disabled:cursor-wait disabled:opacity-45"
                >
                  {responses ? '自分に近い回答者を探す' : '回答データを読み込み中…'}
                </button>
              </div>
            )}

            {step === 'visit' && (
              <fieldset>
                <legend data-step-heading tabIndex={-1} className="text-xl font-bold">あなたは、ATCにどれくらい来ていますか。</legend>
                <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                  {VISIT_OPTIONS.map((option) => (
                    <OptionButton
                      key={option.value}
                      selected={answers.visit === option.value}
                      title={option.label}
                      note={option.note}
                      onClick={() => chooseVisit(option.value)}
                    />
                  ))}
                </div>
              </fieldset>
            )}

            {step === 'age' && (
              <fieldset>
                <legend data-step-heading tabIndex={-1} className="text-xl font-bold">あなたの年代を教えてください。</legend>
                <p className="mt-2 text-sm text-atc-slate">
                  来館頻度が近い回答者を明るく表示しています。
                </p>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {ageBands.map((age) => (
                    <OptionButton
                      key={age}
                      selected={answers.age === age}
                      title={age}
                      onClick={() => chooseAge(age)}
                    />
                  ))}
                </div>
              </fieldset>
            )}

            {step === 'result' && (
              <div>
                <p data-step-heading tabIndex={-1} className="text-sm font-bold text-atc-primary">YOUR COHORT</p>
                <p className="mt-5 text-xl font-bold">あなたに近い回答者は</p>
                <p className="mt-2 text-6xl font-bold tabular-nums text-atc-primary">
                  {(matchCount ?? 0).toLocaleString('ja-JP')}<span className="ml-2 text-xl text-atc-ink">人</span>
                </p>
                <p className="mt-5 text-sm leading-relaxed text-atc-slate">
                  暗くなった点も消えてはいません。ここからは再び全員の声をたどります。
                </p>
                <button
                  type="button"
                  onClick={onComplete}
                  className="mt-8 w-full rounded-full bg-atc-navy px-6 py-4 font-bold text-white transition hover:bg-atc-primary"
                >
                  第1章へ
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={onComplete}
              className="mt-6 text-sm font-bold text-atc-slate underline decoration-atc-slate/40 underline-offset-4 hover:text-atc-primary"
            >
              回答せずに進む
            </button>
            {mode === 'presenter' && <p className="mt-3 text-xs text-atc-slate">プレゼンターモード：次へ進むと矢印キーが有効になります。</p>}
          </div>
        </div>
      </section>
    </main>
  );
}
